/**
 * INTERVIEW ASSISTANT — one panel for the questions, scores and transcript, beside the recording.
 *
 * It replaces the two lists that repeated the same questions (the per-question score list and the transcript
 * card). Each question is one row: its score, a short preview of the answer, and a play control that seeks the
 * recording to that answer. "Show full answer" opens the evidence: the full answer, why the QUESTION scored what
 * it did, then the competency quotes the answer fed. The question on screen opens itself, so the transcript
 * follows the playhead; the interview-score headline lives in its own card above.
 *
 * Where an answer sits in the recording is approximate (only submission times exist — see shared/answerTiming),
 * so a question with no usable time simply has no play control. Content only: accent, tone and fluency are never
 * scored; a human decides.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { MutableRefObject, ReactNode } from "react";
import { seqAt, type AnswerTiming } from "@talenthop/shared";
import type { RecordingControl } from "./Review.js";
import { Info } from "../../components/icons.js";

export interface AssistantItem {
  seq: number;
  kind: string;
  prompt: string;
  promptLocalized?: string | null;
  answerText: string | null;
  verifiedText?: string | null;
  questionScore?: { score: number | null; counts: boolean } | null;
  timing?: AnswerTiming | null;
}

const tone = (score: number | null | undefined): string =>
  score == null ? "n" : score >= 70 ? "hi" : score >= 40 ? "mid" : "low";
const clock = (s: number): string => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const answerOf = (t: AssistantItem): string => (t.verifiedText ?? t.answerText ?? "").trim();

export function InterviewAssistant<T extends AssistantItem>({
  items,
  headline,
  headerRight,
  note,
  controlRef,
  playheadSec,
  hasRecording,
  renderDetail,
}: {
  items: readonly T[];
  /** e.g. "9 of 14 written from your requirement" — or just the question count. */
  headline: string;
  /** The copy menu, supplied by the page. */
  headerRight?: ReactNode;
  note?: ReactNode;
  controlRef: MutableRefObject<RecordingControl | null>;
  /** Seconds into the whole recording right now; null until it has played. */
  playheadSec: number | null;
  hasRecording: boolean;
  renderDetail: (t: T) => ReactNode;
}): ReactNode {
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());
  const [follow, setFollow] = useState(true);
  /** The question the reviewer last clicked, shown in "Now playing" until the playhead says otherwise. */
  const [picked, setPicked] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const timings = useMemo(() => {
    const m = new Map<number, AnswerTiming>();
    for (const t of items) if (t.timing) m.set(t.seq, t.timing);
    return m;
  }, [items]);
  const playing = playheadSec != null ? seqAt(timings, playheadSec) : null;
  const activeSeq = playing ?? picked;

  // Keep the playing row in view. The recording stays put on the left, so the page itself scrolls to it.
  useEffect(() => {
    if (!follow || playing == null) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-seq="${playing}"]`);
    row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [playing, follow]);

  // The question on screen opens itself and the previous one closes, so the transcript follows the video
  // without a separate strip. A reviewer can still open or close any row by hand until the question changes.
  useEffect(() => {
    if (activeSeq != null) setOpen(new Set([activeSeq]));
  }, [activeSeq]);

  const toggle = (seq: number): void =>
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(seq)) n.delete(seq);
      else n.add(seq);
      return n;
    });

  const play = (t: T): void => {
    if (!t.timing || !hasRecording) return;
    setPicked(t.seq);
    setFollow(true);
    controlRef.current?.seekTo(t.timing.startSec);
  };

  return (
    <div className="card t1-ia" aria-label="Interview assistant">
      <div className="card-h">
        <h3>Interview assistant</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="mut sm">{headline}</span>
          {headerRight}
        </div>
      </div>
      {!follow && playing != null && (
        <div className="t1-ia-bar">
          <button type="button" className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={() => setFollow(true)}>
            Follow playback
          </button>
        </div>
      )}
      {note && (
        <p className="t1-rv-note t1-ia-note">
          <Info /> {note}
        </p>
      )}

      <div
        className="t1-ia-list"
        ref={listRef}
        // A reviewer who scrolls the list themselves has taken over: stop pulling the list back.
        onWheel={() => setFollow(false)}
        onTouchMove={() => setFollow(false)}
      >
        {items.length === 0 && <p className="mut sm" style={{ margin: 0 }}>The transcript appears once the interview starts.</p>}
        {items.map((t) => {
          const isOpen = open.has(t.seq);
          const isActive = t.seq === activeSeq;
          const text = answerOf(t);
          const canPlay = hasRecording && !!t.timing;
          return (
            <div key={t.seq} data-seq={t.seq} id={`rv-q-${t.seq}`} className={`t1-ia-row${isActive ? " on" : ""}`}>
              <div className="t1-ia-row-h">
                {canPlay ? (
                  <button type="button" className="t1-ia-play" onClick={() => play(t)} aria-label={`Play question ${t.seq}`} title={`Play this answer from about ${clock(t.timing!.startSec)}`}>
                    {isActive && playing != null ? "●" : "▶"}
                  </button>
                ) : (
                  <span className="t1-ia-play off" aria-hidden="true">·</span>
                )}
                <button
                  type="button"
                  className="t1-ia-q"
                  onClick={() => (canPlay ? play(t) : setPicked(t.seq))}
                  title={canPlay ? "Play this answer" : undefined}
                >
                  <span className="t1-rv-label">Q{t.seq}</span>
                  {t.kind === "adaptive_challenge" && <span className="pill pv">Follow-up</span>}
                  <span className="t1-ia-prompt">{t.promptLocalized ?? t.prompt}</span>
                </button>
                <span className="t1-ia-score">
                  {t.questionScore ? (
                    <>
                      <b className={`tnum ${tone(t.questionScore.score)}`} title={t.questionScore.counts ? undefined : "Shown, not counted in the average"}>
                        {t.questionScore.score ?? "—"}
                      </b>
                      <span className="t1-bt t1-ia-bt" aria-hidden="true">
                        <i className={tone(t.questionScore.score) === "low" ? "low" : tone(t.questionScore.score) === "mid" ? "mid" : ""} style={{ width: `${t.questionScore.score ?? 0}%` }} />
                      </span>
                    </>
                  ) : (
                    <span className="mut sm">—</span>
                  )}
                </span>
              </div>
              {t.timing && <div className="mut sm t1-ia-time">about {clock(t.timing.startSec)}–{clock(t.timing.endSec)}</div>}
              {!isOpen && text && <p className="t1-ia-preview">{text}</p>}
              <button type="button" className="btn sm ghost t1-ia-more" aria-expanded={isOpen} onClick={() => toggle(t.seq)}>
                {isOpen ? "Hide full answer" : "Show full answer"}
              </button>
              {isOpen && <div className="t1-ia-detail">{renderDetail(t)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
